import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { api } from '../../lib/api';

type SubscriptionTier =
  | 'FREE'
  | 'PREMIUM_MONTHLY'
  | 'PREMIUM_YEARLY'
  | 'PREMIUM_PLUS_MONTHLY'
  | 'PREMIUM_PLUS_YEARLY';

interface RevenueCatDetails {
  customerCount: number | null;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  premiumSubscribers: number;
  premiumPlusSubscribers: number;
  byTier: Record<SubscriptionTier, number>;
  byPlatform: Record<string, number>;
  mrrUsd: number;
  arrUsd: number;
  revenue30dUsd: number;
  newSubs7d: number;
  newSubs30d: number;
  churn30d: number;
  cancellations30d: number;
  trialConversion30d: { trials: number; converted: number; pct: number | null };
  generatedAt: string;
}

function fetchDetails(): Promise<RevenueCatDetails> {
  return api.get<RevenueCatDetails>('/admin/health/revenuecat/details');
}

export function RevenueCatDetailsSection(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'revenuecat-details'],
    queryFn: fetchDetails,
    refetchInterval: 5 * 60_000,
  });

  if (isLoading) return <Box>Lädt …</Box>;
  if (error) {
    return (
      <Box dangerous>
        Fehler: {error instanceof Error ? error.message : 'unbekannt'}
      </Box>
    );
  }
  if (!data) return <Box>Keine Daten.</Box>;

  return (
    <article
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">RevenueCat — Details</h3>
        <span className="text-[11px] text-text-tertiary">
          aus eigener DB · {new Date(data.generatedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}
        </span>
      </header>

      <section className="mb-4">
        <SectionTitle>Umsatz</SectionTitle>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat
            label="MRR"
            value={`$${data.mrrUsd.toLocaleString('de-DE', { maximumFractionDigits: 2 })}`}
            hint={`ARR ≈ $${data.arrUsd.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`}
            accent
          />
          <Stat
            label="Revenue 30d"
            value={`$${data.revenue30dUsd.toLocaleString('de-DE', { maximumFractionDigits: 2 })}`}
            hint="Summe aller Purchase + Renewal Events"
          />
          <Stat
            label="Customers (RC)"
            value={
              data.customerCount != null
                ? data.customerCount.toLocaleString('de-DE')
                : '—'
            }
            hint="aus RevenueCat-API"
          />
        </div>
      </section>

      <section className="mb-4">
        <SectionTitle>Subscriptions</SectionTitle>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Aktiv"
            value={data.activeSubscriptions.toLocaleString('de-DE')}
            hint={`Premium ${data.premiumSubscribers} · Premium+ ${data.premiumPlusSubscribers}`}
          />
          <Stat
            label="Trials laufend"
            value={data.trialingSubscriptions.toLocaleString('de-DE')}
          />
          <Stat
            label="Neu (7d / 30d)"
            value={`${data.newSubs7d} / ${data.newSubs30d}`}
            hint="INITIAL_PURCHASE Events"
          />
          <Stat
            label="Churn (30d)"
            value={data.churn30d.toLocaleString('de-DE')}
            hint={`+ ${data.cancellations30d} Cancellations (auto-renew off)`}
            color={data.churn30d > 0 ? darkTheme.colors.warning : undefined}
          />
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <BreakdownTable
          title="Pro Tier"
          rows={Object.entries(data.byTier)
            .filter(([tier, count]) => tier !== 'FREE' && count > 0)
            .map(([tier, count]) => ({ key: shortenTier(tier as SubscriptionTier), count }))}
          total={data.activeSubscriptions}
        />
        <BreakdownTable
          title="Pro Platform"
          rows={Object.entries(data.byPlatform).map(([platform, count]) => ({
            key: platform,
            count,
          }))}
          total={data.activeSubscriptions}
        />
      </div>

      {data.trialConversion30d.pct != null && (
        <section
          className="mt-4 rounded-md border p-3 text-xs"
          style={{
            background: darkTheme.colors.bgElevated,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          <div className="font-semibold text-text-primary">
            Trial-Conversion (30d):{' '}
            <span className="text-accent-light">
              {data.trialConversion30d.pct.toFixed(0)}%
            </span>
          </div>
          <div className="text-text-tertiary">
            {data.trialConversion30d.converted} von {data.trialConversion30d.trials}{' '}
            Trials in zahlende Subscription konvertiert.
          </div>
        </section>
      )}

      <footer
        className="mt-4 border-t pt-2 text-[10px] text-text-tertiary"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        MRR &amp; Revenue werden aus RevenueCat-Webhook-Events berechnet (eigene
        SubscriptionEvent-Tabelle). Active-Subs &amp; Trial-Counts kommen aus der
        Subscription-Tabelle. Customer-Count direkt aus RevenueCat-API.
      </footer>
    </article>
  );
}

function BreakdownTable({
  title,
  rows,
  total,
}: {
  title: string;
  rows: { key: string; count: number }[];
  total: number;
}): JSX.Element {
  if (rows.length === 0) {
    return (
      <section>
        <SectionTitle>{title}</SectionTitle>
        <p className="text-xs text-text-tertiary">Keine aktiven Subs.</p>
      </section>
    );
  }
  const sorted = [...rows].sort((a, b) => b.count - a.count);
  return (
    <section>
      <SectionTitle>{title}</SectionTitle>
      <div className="space-y-1">
        {sorted.map((r) => {
          const pct = total > 0 ? (r.count / total) * 100 : 0;
          return (
            <div key={r.key} className="text-xs">
              <div className="mb-0.5 flex items-baseline justify-between gap-2">
                <span className="text-text-secondary">{r.key}</span>
                <span className="tabular text-text-primary">
                  {r.count} · {pct.toFixed(0)}%
                </span>
              </div>
              <div
                className="h-1 overflow-hidden rounded"
                style={{ background: darkTheme.colors.bgElevated }}
              >
                <div
                  className="h-full"
                  style={{
                    width: `${Math.min(100, pct)}%`,
                    background: darkTheme.colors.accent,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  color,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
  accent?: boolean;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-2"
      style={{
        borderColor: accent ? darkTheme.colors.accent : darkTheme.colors.borderSubtle,
        background: accent ? darkTheme.colors.accentSubtle : 'transparent',
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div
        className="mt-0.5 text-base font-bold tabular"
        style={{ color: color ?? darkTheme.colors.textPrimary }}
      >
        {value}
      </div>
      {hint && <div className="text-[10px] text-text-tertiary">{hint}</div>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </div>
  );
}

function Box({ children, dangerous }: { children: React.ReactNode; dangerous?: boolean }): JSX.Element {
  return (
    <div
      className="rounded-md border p-6 text-sm"
      style={{
        background: dangerous ? darkTheme.colors.dangerBg : darkTheme.colors.bgSurface,
        color: dangerous ? darkTheme.colors.danger : darkTheme.colors.textTertiary,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      {children}
    </div>
  );
}

function shortenTier(tier: SubscriptionTier): string {
  switch (tier) {
    case 'PREMIUM_MONTHLY':
      return 'Premium · monatlich';
    case 'PREMIUM_YEARLY':
      return 'Premium · jährlich';
    case 'PREMIUM_PLUS_MONTHLY':
      return 'Premium+ · monatlich';
    case 'PREMIUM_PLUS_YEARLY':
      return 'Premium+ · jährlich';
    case 'FREE':
      return 'Free';
    default:
      return tier;
  }
}
